import React, {useState} from 'react'

import {Button, Modal, Form, Input, message, Typography, Popconfirm, Tooltip, Tag} from 'antd'
import {SaveOutlined, DeleteOutlined, FormOutlined} from '@ant-design/icons'

import styles from './SaveQueryToggle.module.scss'
import buttonStyles from '../../components/common/styled/Buttons.module.scss'

import {apiFetchUserQuery, apiUpdateQuery} from '../../containers/QueryBuilder/queryBuilderApi'
import {UserQuery} from '../../containers/QueryBuilder/types/UserQuery'

interface SaveQueryToggleProps {
    queryId: string | undefined
    userQuery: UserQuery
    setUserQuery: (userQuery: UserQuery) => void
    isQueryResultAvailable?: boolean
    onlyShowSavedQueryEditButton: boolean
}

interface SavedQueryEditButtonProps {
    userQuery: UserQuery
    isQueryResultAvailable?: boolean
    showSaveQueryModal: () => void
}

const SavedQueryEditButton = ({userQuery, isQueryResultAvailable, showSaveQueryModal}: SavedQueryEditButtonProps) => {

    let isQuerySaved = userQuery.saved

    if (!isQueryResultAvailable) return null
    return (
        <div className={styles.editSavedQueryContainer}>
            <Tag className={styles.tagLabel}>{!isQuerySaved ? 'Add a name and/or a category for this query' : 'Query Name'}</Tag>
            <Tooltip title={userQuery.label}>
                {!isQuerySaved
                    ? null
                    : <span className={styles.queryLabelTag}>
                        {userQuery.label}
                    </span>
                }
            </Tooltip>
            {!isQuerySaved
                ? null
                : !userQuery.category
                    ? null
                    : <Tooltip title={userQuery.category}>
                        <>
                            <Tag className={styles.tagLabel}>{'Query Category'}</Tag>
                            <span className={styles.queryLabelTag}>
                                    {userQuery.category}
                                </span>
                        </>
                    </Tooltip>
            }
            <Tooltip title={!isQuerySaved ? 'Add Name or Category' : !userQuery.category ? 'Edit Name or Add Category' : 'Edit'}>
                <span>
                    <FormOutlined className={styles.editIcon} onClick={showSaveQueryModal}/>
                </span>
            </Tooltip>
        </div>
    )
}

export const SaveQueryToggle = ({
                                    queryId,
                                    userQuery,
                                    setUserQuery,
                                    isQueryResultAvailable,
                                    onlyShowSavedQueryEditButton
                                }: SaveQueryToggleProps) => {
    const [isSaveQueryModalOpen, setIsSaveQueryModalOpen] = useState(false)
    const [isDeleteQueryPopupOpen, setIsDeleteQueryPopupOpen] = useState(false)
    const [isQueryUpdating, setIsQueryUpdating] = useState(false)

    const [form] = Form.useForm()
    const {Text} = Typography

    const showSaveQueryModal = () => {
        form.setFieldsValue({queryLabel: userQuery?.label || '', queryCategory: userQuery?.category || ''})
        setIsSaveQueryModalOpen(true)
    }

    const handleSave = () => {
        form.submit()
    }

    const handleSaveSubmit = async (values: { queryLabel: string; queryCategory: string }) => {

        if (!queryId) return

        setIsQueryUpdating(true)

        const userQueryUpdate = {
            queryId: queryId,
            label: values.queryLabel,
            category: values.queryCategory,
            saved: true
        }

        try {
            await apiUpdateQuery(queryId, userQueryUpdate)
            const fetchedQuery = await apiFetchUserQuery(queryId)
            if (fetchedQuery?.queryId === queryId && fetchedQuery.saved) {
                setUserQuery(fetchedQuery)
                message.success('Query saved')
                setIsSaveQueryModalOpen(false)
            }
        } catch (error) {
            message.error('Query could not be saved. Please try again')
        } finally {
            setIsQueryUpdating(false)
        }

    }

    const handleSaveCancel = () => {
        form.resetFields()
        setIsSaveQueryModalOpen(false)
    }

    const handleDelete = async () => {

        if (!queryId) return

        setIsQueryUpdating(true)

        const userQueryUpdate = {queryId: queryId, saved: false}

        try {
            await apiUpdateQuery(queryId, userQueryUpdate)
            const fetchedQuery = await apiFetchUserQuery(queryId)
            if (fetchedQuery?.queryId === queryId && !fetchedQuery.saved) {
                setUserQuery(fetchedQuery)
                message.success('Query deleted')
                setIsDeleteQueryPopupOpen(false)
            }
        } catch (error) {
            message.error('Query could not be deleted. Please try again')
        } finally {
            setIsQueryUpdating(false)
        }
    }

    return (
        <>
            {onlyShowSavedQueryEditButton
                ?
                (<SavedQueryEditButton
                    userQuery={userQuery}
                    isQueryResultAvailable={isQueryResultAvailable}
                    showSaveQueryModal={showSaveQueryModal}
                    data-testid='open-pencil-save-query-modal'
                />)
                :
                userQuery.saved ?
                    (
                        //region delete button
                        <Popconfirm
                            title={
                                <span><strong>This will permanently delete this saved query. Do you wish to continue?</strong></span>
                            }
                            okText={'Delete'}
                            cancelText={'Cancel'}
                            showCancel={true}
                            onConfirm={handleDelete}
                            okButtonProps={{loading: isQueryUpdating, danger: true}}
                            open={isDeleteQueryPopupOpen}
                            onOpenChange={(visible) => {
                                if (!isQueryUpdating) {
                                    setIsDeleteQueryPopupOpen(visible)
                                }
                            }}
                        >
                            <Button className={buttonStyles.deleteQueryButton}
                                    icon={<DeleteOutlined className={styles.buttonSaveIcon}/>}
                            >
                                Delete Saved Query
                            </Button>
                        </Popconfirm>
                        //endregion delete button

                    ) : (
                        //region save button
                        <Button
                            className={buttonStyles.queryBuilderButton}
                            onClick={showSaveQueryModal}
                            icon={<SaveOutlined className={styles.buttonSaveIcon}/>}
                            data-testid='open-save-query-modal'
                        >
                            Save Query
                        </Button>
                        //endregion save button
                    )}

            <Modal
                title={
                    <>
                        <SaveOutlined className={styles.modalSaveIcon}/>
                        <Text strong>Save Query</Text>
                    </>
                }
                open={isSaveQueryModalOpen}
                onOk={handleSave}
                onCancel={handleSaveCancel}
                okText={'Save'}
                confirmLoading={isQueryUpdating}
                className={styles.saveQueryModal}
                zIndex={1030}
            >
                <Form
                    form={form}
                    layout='horizontal'
                    onFinish={handleSaveSubmit}
                    labelAlign={'left'}
                    labelCol={{span: 6}}
                    wrapperCol={{span: 18}}
                >
                    <Form.Item
                        name='queryLabel'
                        label='Query Name'
                        rules={[
                            {required: true, message: 'Please enter a query name'},
                            {whitespace: true, message: 'Name cannot be empty'},
                            {max: 100, message: 'Name cannot exceed 100 characters'},
                            {
                                pattern: /^[^<>"';\\]*$/,
                                message: 'Name contains invalid characters (< > " \' ; \\)'
                            }
                        ]}
                    >
                        <Input placeholder='Enter a name for this query (Required)'/>
                    </Form.Item>
                    <Form.Item
                        name='queryCategory'
                        label='Query Category'
                        rules={[
                            {max: 100, message: 'Category cannot exceed 100 characters'},
                            {
                                pattern: /^[^<>"';\\]*$/,
                                message: 'Category contains invalid characters (< > " \' ; \\)'
                            }
                        ]}
                    >
                        <Input placeholder='Enter a category for this query (Optional)'/>
                    </Form.Item>
                    {/* adding a hidden button, required to allow 'Enter' key press to submit form*/}
                    <Form.Item style={{display: 'none'}}>
                        <Button htmlType='submit'/>
                    </Form.Item>
                </Form>
            </Modal>

        </>
    )
}

export default SaveQueryToggle